import posthog from 'posthog-js'
import type { SubscriptionPlan } from '@/lib/subscription'

export interface AnalyticsEventPayloads {
  referral_shared: {
    referral_code: string
    channel: 'twitter' | 'email' | 'native'
  }
  referral_link_copied: {
    referral_code: string
  }
  notification_sent: {
    notification_id: string
    notification_type: string
    has_action: boolean
  }
  notification_read: {
    notification_id: string
    notification_type: string
    time_to_read_seconds?: number
  }
  notification_clicked: {
    notification_id: string
    notification_type: string
    action_url?: string
  }
  notification_dismissed: {
    notification_id: string
    notification_type: string
  }
  plan_upgrade_prompt_clicked: {
    feature: string
    current_plan: SubscriptionPlan
    required_plan: SubscriptionPlan
  }
  pwa_install_prompt_shown: undefined
  pwa_install_prompt_accepted: {
    platform?: string
  }
  pwa_install_prompt_dismissed: {
    platform?: string
  }
  network_status_changed: {
    is_online: boolean
  }
  feedback_submitted: {
    message_length: number
    has_email: boolean
  }
  usage_weekly_recap_toggle: {
    enabled: boolean
  }
  usage_weekly_recap_preview: {
    notifications: number
    enabled: boolean
  }
  onboarding_task_completed: {
    task_id: string
    auto: boolean
  }
  onboarding_completed: {
    total_tasks: number
  }
  plan_changed: {
    from_plan: SubscriptionPlan
    to_plan: SubscriptionPlan
    change_type: 'upgrade' | 'downgrade' | 'cancellation'
  }
  shell_action_invoked: {
    action: string
    mode: string
  }
}

export type AnalyticsEventName = keyof AnalyticsEventPayloads

type PayloadArgs<T extends AnalyticsEventName> = AnalyticsEventPayloads[T] extends void
  ? []
  : [payload: AnalyticsEventPayloads[T]]

const isPostHogReady = () => typeof window !== 'undefined' && posthog.__loaded

const safeCapture = (event: string, properties?: Record<string, unknown>) => {
  if (!isPostHogReady()) return
  posthog.capture(event, properties)
}

export function trackEvent<T extends AnalyticsEventName>(name: T, ...payload: PayloadArgs<T>) {
  const [properties] = payload as PayloadArgs<T>
  safeCapture(name, properties as Record<string, unknown> | undefined)
}

export const trackReferralShared = (payload: AnalyticsEventPayloads['referral_shared']) =>
  trackEvent('referral_shared', payload)

export const trackReferralLinkCopied = (payload: AnalyticsEventPayloads['referral_link_copied']) =>
  trackEvent('referral_link_copied', payload)

export const trackPwaPromptShown = () => trackEvent('pwa_install_prompt_shown')

export const trackPwaPromptAccepted = (
  payload: AnalyticsEventPayloads['pwa_install_prompt_accepted']
) => trackEvent('pwa_install_prompt_accepted', payload)

export const trackPwaPromptDismissed = (
  payload?: AnalyticsEventPayloads['pwa_install_prompt_dismissed']
) => trackEvent('pwa_install_prompt_dismissed', payload ?? {})

export const trackNetworkStatusChanged = (
  isOnline: AnalyticsEventPayloads['network_status_changed']['is_online']
) => trackEvent('network_status_changed', { is_online: isOnline })
